import {
  // Autocomplete,
  Box,
  Button,
  Dialog,
  DialogContent,
  // DialogTitle,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Slide,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import { styled, alpha } from "@mui/material/styles"
import { styles } from "./styles/LibraryModal.styles"
import React, { useEffect, useCallback } from "react"
import CloseIcon from "@mui/icons-material/Close"
import Image from "components/Image"
import StarIcon from "@mui/icons-material/Star"
import { useTranslation } from "react-i18next"
import { useAuthContext } from "contexts/AuthContext"
import LibraryItem from "./LibraryItem"
import useApi from "modules/library"
import ModuleProvider from "contexts/ModuleContext"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import TextInput from "components/UI/TextInput"
import Loading from "components/Loading"
import { maxWidth600 } from "themes/default/breakpoints"
import { ACTIVITY_VIEW_MODES } from "pages/student/activity/common/constants"
import useTour from "hooks/useTour"
import { secondToolsGetStarted } from "components/ActivityContent/Activity.GetStarted"

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />
})

const StyledMenu = styled((props) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: "bottom",
      horizontal: "right",
    }}
    transformOrigin={{
      vertical: "top",
      horizontal: "right",
    }}
    {...props}
  />
))(({ theme }) => ({
  "& .MuiPaper-root": {
    backgroundColor: theme.palette.background.default,
    borderRadius: "10px",
    overflow: "hidden",
    border: "1px solid rgba(192, 0, 0, 0.25)",
    marginTop: theme.spacing(1),
    minWidth: 180,
    color:
      theme.palette.mode === "light"
        ? "rgb(55, 65, 81)"
        : theme.palette.grey[300],
    boxShadow: `${theme.palette.background.default} 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px`,
    "& .MuiMenu-list": {
      padding: "4px 0",
    },
    "& .MuiMenuItem-root": {
      textAlign: "center",
      "&:active": {
        backgroundColor: alpha(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity
        ),
      },
    },
  },
}))

export default function LibraryModal({
  open,
  onClose,
  withTour,
  fontSettings,
}) {
  const { t } = useTranslation()
  const { setIsOpen, setSteps, setCurrentStep, setGetStartedViewed } = useTour()
  const { userData } = useAuthContext()
  const { getLibrary, isFetching } = useApi()
  const [data, setData] = React.useState([])
  const [displayFavorites, setDisplayFavorites] = React.useState(false)
  const [search, setSearch] = React.useState("")
  const [moduleType, setModuleType] = React.useState(-1)
  const matches = useMediaQuery(maxWidth600)
  const moduleTypes = [
    {
      label: matches ? t("all") : t("all_modules"),
      value: -1,
    },
    {
      label: t("vocabulary"),
      value: 1,
    },
    {
      label: t("grammar"),
      value: 2,
    },
    {
      label: t("culture"),
      value: 6,
    },
    {
      label: t("article"),
      value: 15,
    },
    {
      label: t("videos"),
      value: 14,
    },
    {
      label: t("comic"),
      value: 26,
    },
    {
      label: t("others"),
      value: 0,
    },
  ]
  const t1 = React.useRef()
  const theme = useTheme()

  useEffect(() => {
    const controller = new AbortController()
    if (open) {
      loadLibraryData(controller)
        .catch(console.error)
        .then((data) => {
          if (!withTour || data.length === 0) {
            return
          }
          setCurrentStep(5)
          setSteps(secondToolsGetStarted({}))
          t1.current = setTimeout(() => {
            setCurrentStep(5)
            setSteps(secondToolsGetStarted({}))
            setIsOpen(true)
          }, 500)
        })
    } else {
      setData([])
      setDisplayFavorites(false)
      setSearch("")
      setModuleType(-1)
      clearTimeout(t1.current)
    }
    return () => {
      clearTimeout(t1.current)
      // cancel the request
      controller?.abort()
    }
  }, [open])

  useEffect(() => {
    if (open) {
      getLibrary({
        moduleTypeId: moduleType,
        onlyFavorites: displayFavorites,
        search,
      }).then((data) => {
        setData(data)
      })
    }
  }, [displayFavorites, moduleType])

  const loadLibraryData = useCallback(async (controller) => {
    const response = await getLibrary({
      moduleTypeId: -1,
      onlyFavorites: displayFavorites,
      search,
      controller,
    })
    setData(response)
    return response
  }, [])

  const [anchorEl, setAnchorEl] = React.useState(null)
  const open2 = Boolean(anchorEl)
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const onSearch = (clear) => {
    getLibrary({
      moduleTypeId: moduleType,
      onlyFavorites: displayFavorites,
      search: clear ? "" : search,
    }).then((data) => {
      setData(data)
    })
  }

  const close = useCallback(() => {
    setIsOpen(false)
    onClose()
    if (withTour) {
      setGetStartedViewed("actions_tools_explanation_overview")
    }
  }, [setIsOpen, withTour])

  return (
    <ModuleProvider viewMode={ACTIVITY_VIEW_MODES.EXPLANATION}>
      <Dialog
        open={open}
        onClose={close}
        TransitionComponent={Transition}
        fullWidth
        PaperProps={{
          sx: [
            styles.container,
            fontSettings.fontFamily && {
              "& .MuiTypography-root, button, textarea, input": {
                fontFamily: fontSettings.fontFamily,
              },
            },
            fontSettings?.bgColor && {
              backgroundColor: fontSettings.bgColor,
            },
          ],
        }}
      >
        <IconButton aria-label="close" onClick={close} sx={styles.closeButton}>
          <CloseIcon fontSize="large" />
        </IconButton>
        <Image name="library" sx={styles.mainImage} />
        <DialogContent
          sx={[
            styles.content,
            fontSettings?.bgColor && {
              backgroundColor: fontSettings.bgColor,
            },
          ]}
        >
          {open && isFetching && <Loading />}
          <Stack
            direction={"row"}
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <Box
              flex={{
                xs: 1 / 3,
                sm: 1,
              }}
            >
              <Button
                variant="contained"
                startIcon={
                  <StarIcon color={!displayFavorites ? "white" : "warning"} />
                }
                disableElevation
                sx={[styles.libraryButton]}
                onClick={() => {
                  setDisplayFavorites(!displayFavorites)
                }}
              >
                {!matches && t("modules")}
              </Button>
            </Box>
            {!matches && (
              <Box flex={1} display={"flex"} justifyContent={"center"}>
                <Typography
                  variant="span"
                  fontWeight={600}
                  textAlign={"center"}
                  sx={{
                    color: theme.palette.common.black,
                    opacity: 0.75,
                  }}
                >
                  {t("user_library", {
                    username: `${userData.name} ${userData?.lastName}`,
                  })}
                </Typography>
              </Box>
            )}
            <Stack
              direction={"row"}
              flex={1}
              justifyContent={"flex-end"}
              alignItems={"center"}
              spacing={1}
            >
              <Button
                variant="contained"
                endIcon={<KeyboardArrowDownIcon />}
                disableElevation
                sx={styles.libraryButton}
                onClick={handleClick}
              >
                {moduleTypes.find((item) => item.value === moduleType)?.label ||
                  t("all")}
              </Button>
              <StyledMenu
                id="demo-customized-menu"
                MenuListProps={{
                  "aria-labelledby": "demo-customized-button",
                }}
                anchorEl={anchorEl}
                open={open2}
                onClose={handleClose}
              >
                {moduleTypes.map((item, index) => {
                  return (
                    <MenuItem
                      key={index}
                      onClick={() => {
                        handleClose()
                        setModuleType(item.value)
                      }}
                      disableRipple
                      sx={[
                        styles.menuItem,
                        fontSettings?.fontFamily && {
                          fontFamily: fontSettings.fontFamily,
                        },
                      ]}
                    >
                      {item.label}
                    </MenuItem>
                  )
                })}
              </StyledMenu>
              <Box
                display={"flex"}
                alignItems={"center"}
                position={"relative"}
                justifyContent={"flex-end"}
                sx={styles.searchBox}
              >
                <TextInput
                  placeholder={t("search")}
                  InputProps={{
                    sx: styles.searchInput,
                    startAdornment:
                      search.length > 0 ? (
                        <InputAdornment position="start">
                          <IconButton
                            size="small"
                            sx={{ padding: 0 }}
                            onClick={() => {
                              if (search.length > 0) {
                                onSearch(true)
                              }
                              onSearch()
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                  }}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <IconButton
                  sx={styles.inputButton}
                  onClick={() => {
                    onSearch()
                  }}
                >
                  {t("go")}
                </IconButton>
              </Box>
            </Stack>
          </Stack>

          <Stack direction={"column"} spacing={1} mt={2}>
            {data.map((item, index) => (
              <LibraryItem
                key={index}
                item={item}
                onClose={close}
                showTour={withTour}
                fontSettings={fontSettings}
              />
            ))}
          </Stack>
        </DialogContent>
      </Dialog>
    </ModuleProvider>
  )
}
